import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MessageSquare, Plus, Eye, ArrowRight } from 'lucide-react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { SupportModal } from './SupportModal';
import { useNavigate } from 'react-router-dom';

interface SupportOptionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SupportOptionsModal({ open, onOpenChange }: SupportOptionsModalProps) {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [showNewSupportModal, setShowNewSupportModal] = useState(false);

  // Track authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const handleViewSupport = () => {
    if (user) {
      // User is logged in, go to support page
      navigate('/support');
      onOpenChange(false);
    } else {
      // User not logged in, redirect to login
      navigate('/login');
      onOpenChange(false);
    }
  };

  const handleNewSupport = () => {
    // Close options modal and open new support modal
    onOpenChange(false);
    setShowNewSupportModal(true);
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              Support Options
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Choose an option below to get the help you need:
            </p>

            {/* Option 1: View Your Support */}
            <Card className="cursor-pointer transition-all hover:shadow-md hover:border-blue-300" onClick={handleViewSupport}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between text-base">
                  <div className="flex items-center gap-2">
                    <Eye className="h-5 w-5 text-blue-600" />
                    Your Support Requests
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </CardTitle>
                <CardDescription>
                  {user
                    ? "View and track your existing support requests, see queue position, and monitor progress"
                    : "Login to view and track your support requests with real-time status updates"
                  }
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {user ? (
                    <>
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      <span>Logged in as {user.email}</span>
                    </>
                  ) : (
                    <>
                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                      <span>Login required</span>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Option 2: New Support Message */}
            <Card className="cursor-pointer transition-all hover:shadow-md hover:border-green-300" onClick={handleNewSupport}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between text-base">
                  <div className="flex items-center gap-2">
                    <Plus className="h-5 w-5 text-green-600" />
                    New Support Request
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </CardTitle>
                <CardDescription>
                  Submit a new support request, report an issue, or ask a question to our support team
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  <span>Available for everyone</span>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={handleClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleNewSupport}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Quick Submit
              </Button>
            </div>
          </div>

          <div className="text-xs text-muted-foreground text-center pt-2 border-t">
            We typically respond within 24 hours during business days.
          </div>
        </DialogContent>
      </Dialog>

      {/* New Support Modal */}
      <SupportModal 
        open={showNewSupportModal} 
        onOpenChange={setShowNewSupportModal} 
      />
    </>
  );
}
